import React, { useState, useEffect, useCallback, useRef } from "react";
import { Search } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { streamClient } from "../../config/stream";
import Sidebar from "../../components/Sidebar";
import { ClipLoader } from "react-spinners";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebaseConfig";
import { useParams } from "react-router-dom";
import CustomChatComponent from "../../components/ChatComponent";
import { safeMarkChannelRead } from "../../services/streamConnectionService";
// Channel name updates handled by streamChannelHelpers now
import { getChannelDisplayName } from "../../services/streamService";
import { getUserChannels } from "../../api/examPrepApi";
import { useTranslation } from "react-i18next";

const MessagesUser = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [selectedChatInfo, setSelectedChatInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("standard");
  const [searchQuery, setSearchQuery] = useState("");
  const [unreadCounts, setUnreadCounts] = useState({});
  const [groupNames] = useState({});
  const { channelId: urlChannelId } = useParams();
  const [streamClientConnected, setStreamClientConnected] = useState(false);
  const [groupMemberCounts, setGroupMemberCounts] = useState({});
  
  // Track if we've already initialized the tab for the current URL channel
  // This prevents forcing tab changes when user manually switches tabs
  const tabInitializedRef = useRef(false);
  const channelSelectedRef = useRef(false);
  const lastUrlChannelIdRef = useRef(null);

  useEffect(() => {
    const savedActiveTab = localStorage.getItem("activetab");
    if (savedActiveTab) {
      setActiveTab(savedActiveTab);
    }
    
    // Reset refs when URL channel changes
    if (lastUrlChannelIdRef.current !== urlChannelId) {
      tabInitializedRef.current = false;
      channelSelectedRef.current = false;
      lastUrlChannelIdRef.current = urlChannelId;
    }
  }, [urlChannelId]);

  const handleChannelLeave = (channelId) => {
    setChannels((prevChannels) =>
      prevChannels.filter((channel) => channel.id !== channelId),
    );

    if (selectedChannel?.id === channelId) {
      const remainingChannels = channels.filter(
        (channel) => channel.id !== channelId,
      );
      setSelectedChannel(
        remainingChannels.length > 0 ? remainingChannels[0] : null,
      );
      if (remainingChannels.length > 0) {
        const firstChannel = remainingChannels[0];
        const otherUser = getOtherUserFromChannel(firstChannel);
        setSelectedChatInfo(otherUser);
      } else {
        setSelectedChatInfo(null);
      }
    }
  };
  // Helper function to extract the other user's info from a channel
  const getOtherUserFromChannel = useCallback(
    (channel) => {
      if (!channel || !user) return null;
      // For exam_prep channels, use created_by as the other user (tutor)
      if (channel.type === "exam_prep" && channel.created_by) {
        return {
          id: channel.created_by.id,
          name: channel.created_by.name,
          image: channel.created_by.image,
          online: channel.created_by.online || false,
        };
      }
      // For one-to-one chats, always show the other user's name
      if (channel.type === "one_to_one_chat") {
        const members = Object.values(channel.state?.members || {});
        const otherMember = members.find(
          (member) => member.user?.id !== user.uid,
        );
        if (otherMember && otherMember.user) {
          return {
            id: otherMember.user.id,
            name: otherMember.user.name,
            image: otherMember.user.image,
            online: otherMember.user.online || false,
          };
        }
      }
      // For group/class chats, always use channel.data.name
      return {
        id: channel.id,
        name: channel.data?.name || channel.name || "Group",
        image: channel.data?.image || "/default-avatar.png",
        online: false,
      };
    },
    [user],
  );
  // Ensure Stream client is connected
  useEffect(() => {
    let isMounted = true;

    const connectStreamClient = async () => {
      if (!user || !isMounted) return;

      try {
        // Check if already connected to the correct user
        if (streamClient.userID === user.uid && streamClient.isConnected) {
          if (isMounted) {
          setStreamClientConnected(true);
          }
          return;
        }

        // Use ensureStreamConnection to handle connection properly
        const { ensureStreamConnection, setLastUserId } = await import(
          "../../services/streamConnectionService"
        );
        setLastUserId(user.uid);

        // Just ensure connection - no need to verify, ensureStreamConnection handles it
        await ensureStreamConnection(async () => {
          return true; // Operation succeeds if we get here
        }, user.uid);

        if (isMounted) {
        setStreamClientConnected(true);
        }
      } catch (error) {
        if (!isMounted) return;

        console.warn(
          `[MessagesUser] Stream connection warning: ${error.message}`,
        );
        // Don't set connected to false - let other operations try
        // The connection might still work for queries even if this check fails
      }
    };

    connectStreamClient();

    return () => {
      isMounted = false;
    };
  }, [user]);

  useEffect(() => {
    const fetchUserChannels = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);

        // getUserChannels now queries GetStream directly and enriches with Firestore data
        const channels = await getUserChannels(user.uid);

        // All channels should now have proper data from getUserChannels
        // Verify all have data property and log any missing
        const channelsWithoutData = channels.filter((ch) => !ch.data);
        if (channelsWithoutData.length > 0) {
          console.warn(
            `[MessagesUser] ${channelsWithoutData.length} channels still missing data property after enrichment`,
          );
        }

        setChannels(channels);
      } catch (err) {
        console.error("[MessagesUser] Error fetching channels:", err);
        setChannels([]);
      } finally {
        setLoading(false);
      }
    };
    fetchUserChannels();
  }, [user]);

  // Listen for changes in user's joinedGroups to refresh channels
  useEffect(() => {
    const refreshChannelsOnGroupChange = async () => {
      if (!user || !user.joinedGroups) return;

      try {
        const channels = await getUserChannels(user.uid);
        setChannels(channels);
      } catch (err) {
        console.error("Error refreshing channels on group change:", err);
      }
    };

    // Listen for custom group join events
    const handleGroupJoin = async (event) => {
      const { userId } = event.detail;
      if (userId === user.uid) {
        // Refresh channels when this user joins a group
        try {
          const channels = await getUserChannels(user.uid);
          setChannels(channels);
        } catch (err) {
          console.error("Error refreshing channels on group join:", err);
        }
      }
    };

    // Add event listeners for group join events
    window.addEventListener("groupJoined", handleGroupJoin);
    window.addEventListener("userGroupsUpdated", handleGroupJoin);

    const interval = setInterval(refreshChannelsOnGroupChange, 30000); // Check every 30 seconds

    return () => {
      window.removeEventListener("groupJoined", handleGroupJoin);
      window.removeEventListener("userGroupsUpdated", handleGroupJoin);
      clearInterval(interval);
    };
  }, [user?.joinedGroups, user]);

  useEffect(() => {
    if (!channels.length) return;
    // Only fetch for group chats
    const groupChannels = channels.filter(
      (ch) => ch.type === "standard_group" || ch.type === "premium_group",
    );
    groupChannels.forEach(async (channel) => {
      try {
        const groupRef = doc(db, "groups", channel.id);
        const groupDoc = await getDoc(groupRef);
        if (groupDoc.exists()) {
          const groupData = groupDoc.data();
          setGroupMemberCounts((prev) => ({
            ...prev,
            [channel.id]: Array.isArray(groupData.memberIds)
              ? groupData.memberIds.length
              : 0,
          }));
        }
      } catch (e) {
        // fallback to 0
        setGroupMemberCounts((prev) => ({ ...prev, [channel.id]: 0 }));
      }
    });
  }, [channels]);

  const handleChannelSelect = useCallback(
    async (channelData, forceTab = null) => {
      if (!streamClientConnected) {
        console.warn(
          "[MessagesUser] Tried to select channel before Stream client connected",
        );
        return;
      }
      
      // If we have a forced tab (from navigation), use it strictly
      // Only fall back to auto-detection if forceTab is null/undefined
      let correctTab = forceTab;
      
      // Only auto-detect if no forced tab was provided
      if (!correctTab || (correctTab !== "standard" && correctTab !== "bammbuu")) {
        const channelType = channelData.type || channelData?.type;
        correctTab = "standard"; // Default to community tab
        
        // Premium groups, premium classes, exam prep, and one-on-one chats with tutors go to tutor tab
        if (
          channelType === "premium_group" ||
          channelType === "premium_individual_class" ||
          channelType === "exam_prep"
        ) {
          correctTab = "bammbuu";
        } else if (channelType === "one_to_one_chat") {
          // Check if it's a chat with a tutor
          const members = Object.values(channelData.state?.members || {});
          const otherMember = members.find(
            (member) => member.user?.id !== user?.uid,
          );
          if (otherMember?.user?.userType === "tutor") {
            correctTab = "bammbuu";
          }
        }
        // standard_group stays as "standard" (default)
      }
      
      // Update tab if it's different from current
      if (activeTab !== correctTab) {
        setActiveTab(correctTab);
        localStorage.setItem("activetab", correctTab);
      }
      
      // Create a real Stream channel instance
      const streamChannel = streamClient.channel(
        channelData.type,
        channelData.id,
      );
      await streamChannel.watch();
      // Ensure user is a member of the channel
      const members = streamChannel.state.members || {};
      if (!members[user.uid]) {
        try {
          await streamChannel.addMembers([user.uid]);
        } catch (err) {
          console.error("[MessagesUser] Error adding user to channel:", err);
        }
      }
      setSelectedChannel(streamChannel);
      setSelectedChatInfo(getOtherUserFromChannel(channelData));
      try {
        await safeMarkChannelRead(streamChannel);
        setUnreadCounts((prev) => ({
          ...prev,
          [channelData.id]: 0,
        }));
      } catch (error) {
        console.error("[MessagesUser] Error marking channel as read:", error);
      }
    },
    [streamClientConnected, user.uid, getOtherUserFromChannel, activeTab],
  );

  // Handle URL-based channel selection
  useEffect(() => {
    // Only initialize tab and select channel on first load or when URL channel changes
    // Don't force tab change when user manually switches tabs
    if (urlChannelId && streamClientConnected && !channelSelectedRef.current) {
      // When navigating via URL, try to determine the correct tab by:
      // 1. Check localStorage (set by button)
      // 2. If not found, check Firestore to see if it's a premium group
      const savedTab = localStorage.getItem("activetab");
      
      // Find the channel with the matching ID
      const targetChannel = channels.find(
        (channel) => channel.id === urlChannelId,
      );
      
      // If channels are loaded and we found the target, select it
      if (targetChannel && channels.length > 0) {
        // Determine the correct tab by checking channel type first, then Firestore if needed
        const determineTab = async () => {
          let tabToUse = null;
          
          // First, check if we have a channel with a type we can use
          const channelType = targetChannel.type;
          if (channelType === "premium_group" || channelType === "premium_individual_class" || channelType === "exam_prep") {
            tabToUse = "bammbuu";
          } else if (channelType === "standard_group") {
            tabToUse = "standard";
          } else if (channelType === "one_to_one_chat") {
            // Check if it's with a tutor
            const members = Object.values(targetChannel.state?.members || {});
            const otherMember = members.find((m) => m.user?.id !== user?.uid);
            tabToUse = otherMember?.user?.userType === "tutor" ? "bammbuu" : "standard";
          }
          
          // If we couldn't determine from channel type, check Firestore
          if (!tabToUse) {
            try {
              const groupDoc = await getDoc(doc(db, "groups", urlChannelId));
              if (groupDoc.exists()) {
                const groupData = groupDoc.data();
                const isPremium = groupData.isPremium;
                tabToUse = isPremium ? "bammbuu" : "standard";
                localStorage.setItem("activetab", tabToUse);
              } else {
                // Not a group, might be a class - check classes
                const classDoc = await getDoc(doc(db, "classes", urlChannelId));
                if (classDoc.exists()) {
                  // Classes are always premium, so go to tutor tab
                  tabToUse = "bammbuu";
                  localStorage.setItem("activetab", tabToUse);
                } else {
                  // Use saved tab if available, otherwise default to standard
                  tabToUse = savedTab || "standard";
                }
              }
            } catch (err) {
              console.error("[MessagesUser] Error checking Firestore:", err);
              tabToUse = savedTab || "standard";
            }
          } else {
            // Save the determined tab for consistency
            localStorage.setItem("activetab", tabToUse);
          }
          
          // Mark as selected before calling handleChannelSelect to prevent re-triggering
          channelSelectedRef.current = true;
          if (!tabInitializedRef.current) {
            tabInitializedRef.current = true;
          }
          
          handleChannelSelect(targetChannel, tabToUse);
        };
        
        determineTab();
      } else if (channels.length > 0) {
        // Channels loaded but target channel not found - might be a one-to-one chat
        const determineTab = async () => {
          const savedTab = localStorage.getItem("activetab");
          let tabToUse = savedTab || "standard";
          
        // Try to query the channel directly from Stream
          try {
            const streamChannel = streamClient.channel(
              "one_to_one_chat",
              urlChannelId,
            );
            await streamChannel.watch();
            channelSelectedRef.current = true;
            if (!tabInitializedRef.current) {
              tabInitializedRef.current = true;
            }
            handleChannelSelect(streamChannel, tabToUse);
          } catch (error) {
            console.error(
              "[MessagesUser] Failed to query channel directly:",
              error,
            );
          }
        };
        determineTab();
      }
    }
  }, [urlChannelId, channels, streamClientConnected, handleChannelSelect, user?.uid]);

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  // Filter channels based on type and search query
  const filterChannels = (channelsToFilter) => {
    return channelsToFilter.filter((channel) => {
      // Safeguard: ensure channel.data exists, if not, add an empty data object
      if (!channel.data || typeof channel.data !== "object") {
        // Add empty data object instead of filtering out - this allows the channel to still appear
        // and getUserChannels will enrich it on next fetch
        channel.data = {};

        // Try to fetch from Firestore as a fallback
        if (
          channel.type === "standard_group" ||
          channel.type === "premium_group"
        ) {
          getDoc(doc(db, "groups", channel.id))
            .then((groupDoc) => {
              if (groupDoc.exists()) {
                const groupData = groupDoc.data();
                channel.data.name = groupData.groupName || "";
                channel.data.description = groupData.groupDescription || "";
                channel.data.image = groupData.imageUrl || "";
                channel.data.groupId = channel.id;
              }
            })
            .catch((err) => {
              console.error(
                `[MessagesUser] Failed to fetch group data for ${channel.id}:`,
                err,
              );
            });
        } else if (channel.type === "premium_individual_class") {
          getDoc(doc(db, "classes", channel.id))
            .then((classDoc) => {
              if (classDoc.exists()) {
                const classData = classDoc.data();
                channel.data.name = classData.className || "";
                channel.data.description = classData.classDescription || "";
                channel.data.image = classData.imageUrl || "";
                channel.data.classId = channel.id;
              }
            })
            .catch((err) => {
              console.error(
                `[MessagesUser] Failed to fetch class data for ${channel.id}:`,
                err,
              );
            });
        }
      }

      let searchName = "";
      if (channel.type === "one_to_one_chat") {
        // Use the other user's name for search
        const members = Object.values(channel.state?.members || {});
        const otherMember = members.find(
          (member) => member.user?.id !== user.uid,
        );
        searchName =
          otherMember && otherMember.user
            ? otherMember.user.name?.toLowerCase() || ""
            : "";
      } else {
        searchName = (channel.data?.name || "").toLowerCase();
      }
      const channelDescription = (
        channel.data?.description || ""
      ).toLowerCase();
      const query = searchQuery.toLowerCase();
      return searchName.includes(query) || channelDescription.includes(query);
    });
  };

  // Helper to check if a one_to_one_chat is with a tutor
  const isTutorChat = (channel) => {
    if (channel.type === "one_to_one_chat") {
      const members = Object.values(channel.state?.members || {});
      const otherMember = members.find(
        (member) => member.user?.id !== user?.uid,
      );
      // Check if the other user is a tutor
      return otherMember?.user?.userType === "tutor";
    }
    return false;
  };

  // Helper to check if a channel is a free community chat
  const isFreeCommunityChat = (channel) => {
    // Only free groups (standard_group) go in community tab
    return channel.type === "standard_group";
  };

  // Categorize channels
  // Community chats: free groups only
  const standardChats = channels.filter((ch) => isFreeCommunityChat(ch));
  
  // Tutor chats: premium groups, premium classes, exam prep, and one-on-one chats with tutors
  const filteredForBammbuu = channels.filter((ch) => {
    // Include premium groups (user has joined)
    if (ch.type === "premium_group") {
      return true;
    }
    // Include premium individual classes
    if (ch.type === "premium_individual_class") {
      return true;
    }
    // Include exam_prep chats (tutor chats)
    if (ch.type === "exam_prep") {
      return true;
    }
    // Include one_to_one_chat only if it's with a tutor
    if (ch.type === "one_to_one_chat") {
      return isTutorChat(ch);
    }
    // Exclude all other types (free groups should be in standard tab)
    return false;
  });

  // Helper to get display name and image for a channel
  const getExamPrepDisplay = (channel) => {
    if (channel.type === "exam_prep" && channel.created_by) {
      return {
        name: channel.created_by.name || "Exam Prep",
        image: channel.created_by.image || "/images/exam-prep-icon.png",
      };
    }
    return {
      name: channel.data?.name || channel.name || "Group",
      image: channel.data?.image || "/default-avatar.png",
    };
  };

  const ChatItem = ({ channel, isInstructor }) => {
    // Exam prep one-to-one chat UI
    if (channel.type === "exam_prep") {
      const { name, image } = getExamPrepDisplay(channel);
      const displayName = `Exam Prep - ${name}`;
      return (
        <div
          key={channel.id}
          className={`flex cursor-pointer items-center gap-3 rounded-3xl border p-3 ${
            selectedChannel?.id === channel.id
              ? "border-[#22bf37] bg-[#f0fdf1]"
              : "border-[#fbbf12] bg-white"
          }`}
          onClick={() => handleChannelSelect(channel)}
        >
          <div className="relative">
            <img
              src={image}
              alt={displayName}
              className="h-12 w-12 rounded-full object-cover"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/default-avatar.png";
              }}
            />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">{displayName}</h3>
            </div>
          </div>
        </div>
      );
    }
    // One-to-one chat UI (unchanged)
    if (
      channel.type === "one_to_one_chat" ||
      channel.type === "premium_individual_class"
    ) {
      const otherUser = getOtherUserFromChannel(channel);
      return (
        <div
          key={channel.id}
          className={`flex cursor-pointer items-center gap-3 rounded-3xl border p-3 ${
            selectedChannel?.id === channel.id
              ? "border-[#22bf37] bg-[#f0fdf1]"
              : "border-[#fbbf12] bg-white"
          }`}
          onClick={() => handleChannelSelect(channel)}
        >
          <div className="relative">
            <img
              src={otherUser?.image || "/default-avatar.png"}
              alt={otherUser?.name}
              className="h-12 w-12 rounded-full object-cover"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/default-avatar.png";
              }}
            />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {getChannelDisplayName(channel, user)}
              </h3>
            </div>
          </div>
          {/* Unread badge for one-to-one chats */}
          {unreadCounts[channel.id] > 0 && (
            <span className="ml-2 flex h-6 w-6 items-center justify-center rounded-full bg-[#14B82C] text-xs text-white">
              {unreadCounts[channel.id]}
            </span>
          )}
        </div>
      );
    }
    // Group chat UI (reverted, but show real member count and new badge)
    const groupName = channel.data?.name || groupNames[channel.id] || "Group";
    const groupImage = channel.data?.image || "/default-avatar.png";
    // Get language from channel.data (enriched from Firestore)
    const groupLanguage = channel.data?.language || "English";

    // Determine flag based on language
    let languageFlag = "/svgs/xs-us.svg";
    if (groupLanguage === "Spanish") {
      languageFlag = "/svgs/xs-spain.svg";
    } else if (
      groupLanguage === "English/Spanish" ||
      groupLanguage === "English-Spanish" ||
      groupLanguage === "English-Spanish Exchange"
    ) {
      languageFlag = "/svgs/eng-spanish-xs.svg";
    }
    const memberCount =
      groupMemberCounts[channel.id] !== undefined
        ? groupMemberCounts[channel.id]
        : "-";
    return (
      <div
        key={channel.id}
        className={`flex cursor-pointer items-center gap-3 rounded-3xl border p-3 ${
          selectedChannel?.id === channel.id
            ? "border-[#fbbf12] bg-[#ffffea]"
            : "border-[#fbbf12] bg-white"
        }`}
        onClick={() => handleChannelSelect(channel)}
      >
        <div className="relative">
          <img
            src={groupImage}
            alt={groupName}
            className="h-12 w-12 rounded-full object-cover"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/default-avatar.png";
            }}
          />
        </div>
        <div className="flex-1">
          <div className="mb-1 flex items-center gap-2">
            <span className="truncate text-lg font-bold">{groupName}</span>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-1">
              <img src={languageFlag} alt={groupLanguage} className="h-5 w-5" />
              <span className="text-sm font-medium text-gray-700">
                {groupLanguage}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <img src="/svgs/users.svg" alt="members" className="h-5 w-5" />
              <span className="text-sm font-medium text-gray-700">
                {memberCount}
              </span>
            </div>
          </div>
        </div>
        {/* Unread badge for group chats */}
        {unreadCounts[channel.id] > 0 && (
          <span className="ml-2 flex h-6 w-6 items-center justify-center rounded-full bg-[#14B82C] text-xs text-white">
            {unreadCounts[channel.id]}
          </span>
        )}
      </div>
    );
  };

  useEffect(() => {
    // Fix channel names for premium channels to ensure they display correctly
    const fixChannelNames = async () => {
      try {
        // Only fix channel names if we have channels and the stream client is connected
        if (channels.length > 0 && streamClientConnected) {
          const premiumChannels = channels.filter(
            (ch) =>
              ch.type === "premium_group" ||
              ch.type === "premium_individual_class",
          );

          // Update names for premium channels - handled automatically by streamChannelHelpers
          // Names are synced when channels are created/updated
          for (const channel of premiumChannels) {
            try {
              // Channel name syncing is now handled automatically
              // No manual update needed
            } catch (error) {
              console.warn(
                `Error fixing channel ${channel.id} name: ${error.message}`,
              );
            }
          }
        }
      } catch (error) {
        console.error("Error in fixChannelNames:", error);
      }
    };

    // Run the fix whenever channels are updated
    fixChannelNames();
  }, [channels, streamClientConnected]);

  if (loading) {
    return (
      <div className="flex min-h-screen bg-white">
        <div className="h-full w-[272px] flex-shrink-0 p-4">
          <Sidebar user={user} />
        </div>
        <div className="flex flex-1 items-center justify-center">
          <ClipLoader color="#14B82C" size={50} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-white">
      {/* Sidebar */}
      <div className="h-full w-[272px] flex-shrink-0 p-4">
        <Sidebar user={user} />
      </div>

      {/* Main Content */}
      <div className="min-w-[calc(100% - 272px)] h-[calc(100vh-0px)] flex-1 overflow-x-auto p-4 pl-0">
        <div className="h-[calc(100vh-32px)] overflow-y-auto rounded-3xl border border-[#e7e7e7] bg-white p-[16px]">
          <div className="mb-6 flex items-center justify-between border-b pb-4">
            <div className="flex items-center gap-4">
              <h1 className="text-4xl font-semibold">Chats</h1>
            </div>
          </div>
          <div className="m-2 flex h-[calc(100vh-157px)] flex-1 rounded-3xl bg-white">
            <div className="flex w-96 flex-col overflow-hidden rounded-2xl bg-[#f6f6f6] p-4">
              <div className="mb-4 flex w-full justify-center sm:w-auto">
                <div className="mb-10 flex w-full justify-center sm:w-auto">
                  <div className="relative inline-flex rounded-full border border-[#888888] bg-white p-1">
                    <div
                      className="absolute left-0 top-0 h-full rounded-full border border-[#042F0C] bg-[#FFBF00] transition-all duration-300 ease-in-out"
                      style={{
                        transform:
                          activeTab === "standard"
                            ? "translateX(0)"
                            : "translateX(82.0%)",
                        width: activeTab === "standard" ? "50.5%" : "55%",
                      }}
                    />
                    <button
                      onClick={() => setActiveTab("standard")}
                      className="relative z-10 whitespace-nowrap rounded-full py-1 pl-4 pr-[22px] text-sm font-medium text-[#042f0c] transition-colors"
                    >
                      {t("chats.tabs.freeChat")}
                    </button>
                    <button
                      onClick={() => setActiveTab("bammbuu")}
                      className="relative z-10 whitespace-nowrap rounded-full px-8 py-1 font-medium text-black transition-colors"
                    >
                      {t("chats.tabs.tutorChats")}
                    </button>
                  </div>
                </div>
              </div>
              <div className="relative mb-4">
                <Search className="absolute left-3 top-3 h-5 w-5 text-[#5d5d5d]" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearch}
                  placeholder={
                    activeTab === "standard"
                      ? t("chats.search.freeChats")
                      : t("chats.search.tutorChats")
                  }
                  className="w-full rounded-3xl border border-gray-200 py-2 pl-12 pr-4 focus:border-[#14B82C] focus:outline-none focus:ring-0"
                />
              </div>{" "}
              <div className="scrollbar-hide flex-1 space-y-2 overflow-y-auto">
                {activeTab === "bammbuu" ? (
                  // For bammbuu+ chats
                  <>
                    {filterChannels(filteredForBammbuu).length > 0 ? (
                      filterChannels(filteredForBammbuu).map((channel) => {
                        return (
                          <ChatItem
                            key={channel.id}
                            channel={channel}
                            isInstructor={
                              channel.type === "premium_individual_class" ||
                              channel.type === "one_to_one_chat"
                            }
                          />
                        );
                      })
                    ) : (
                      <div className="flex h-64 flex-col items-center justify-center">
                        <p className="text-xl font-semibold text-gray-500">
                          {searchQuery
                            ? t("chats.messages.noResults", "No results found")
                            : t("chats.messages.noTutorChatsFound")}
                        </p>
                        <p className="text-sm text-gray-400">
                          {searchQuery
                            ? t(
                                "chats.messages.tryDifferentSearch",
                                "Try a different search term",
                              )
                            : t("chats.messages.joinPremiumGroups")}
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  // Standard chats
                  <>
                    {filterChannels(standardChats).length > 0 ? (
                      filterChannels(standardChats).map((channel) => {
                        return (
                          <ChatItem
                            key={channel.id}
                            channel={channel}
                            isInstructor={false}
                          />
                        );
                      })
                    ) : (
                      <div className="flex h-64 flex-col items-center justify-center">
                        <p className="text-xl font-semibold text-gray-500">
                          {searchQuery
                            ? t("chats.messages.noResults", "No results found")
                            : t("chats.messages.noFreeChatsFound")}
                        </p>
                        <p className="text-sm text-gray-400">
                          {searchQuery
                            ? t(
                                "chats.messages.tryDifferentSearch",
                                "Try a different search term",
                              )
                            : t("chats.messages.joinFreeGroups")}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="ml-4 flex-1">
              {streamClientConnected && selectedChannel ? (
                <>
                  <CustomChatComponent
                    channelId={selectedChannel.id}
                    type={selectedChannel.type}
                    onChannelLeave={handleChannelLeave}
                    chatInfo={selectedChatInfo}
                    description={
                      selectedChannel.data?.description ||
                      selectedChannel.data?.name ||
                      ""
                    }
                    name={
                      selectedChannel.type === "exam_prep"
                        ? `Exam Prep - ${selectedChatInfo?.name || "Tutor"}`
                        : getChannelDisplayName(selectedChannel, user)
                    }
                  />
                </>
              ) : (
                <div className="flex h-full items-center justify-center text-gray-500">
                  {streamClientConnected
                    ? t("chats.messages.selectChat")
                    : t("chats.messages.loading")}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagesUser;
